import { Component, OnDestroy, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { HeaderComponent } from "./shared/component/header/header.component";
import { FooterComponent } from "./shared/component/footer/footer.component";
import { CommonModule, ViewportScroller } from '@angular/common';
import { AppStateService } from './shared/services/app-state.service';
import { filter, Subject, take, takeUntil } from 'rxjs';
import { ToastModule } from "primeng/toast";

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, HeaderComponent, FooterComponent, CommonModule, ToastModule],
  templateUrl: './app.component.html',
})
export class AppComponent implements OnDestroy {
  title = 'y4j';
  private lastBaseUrl = '';
  private readonly destroy$ = new Subject<void>();

  /** Signal to track if current route is a learner route */
  isLearnerRoute = signal(false);
  
  /** Signal to track if current route is the login page */
  isLoginRoute = signal(false);

  /** Signal to track if current route is an admin route */
  isAdminRoute = signal(false);

  /** Signal to track if current route is a trainer route */
  isTrainerRoute = signal(false);

  /** Signal to track if layout is ready (first navigation complete) - prevents FOUC */
  isLayoutReady = signal(false);

  constructor(
    public appStateService: AppStateService,
    private readonly router: Router,
    private readonly viewportScroller: ViewportScroller,
  ) {
    this.handleScroll();
    this.trackLearnerRoutes();
    this.waitForFirstNavigation();
  }

  /**
   * Wait for the first NavigationEnd event before showing content.
   * This prevents FOUC (Flash of Unstyled Content) during page reload
   * by ensuring the lazy-loaded layout component has started rendering.
   */
  private waitForFirstNavigation() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        take(1)
      )
      .subscribe(() => {
        // Small delay to allow the layout component to render
        setTimeout(() => this.isLayoutReady.set(true), 50);
      });
  }

  private handleScroll() {
    this.router.events
      .pipe(
        filter((event: any): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(event => {
        const currentUrl = event?.url;
        const previousUrl = this.lastBaseUrl;
        this.lastBaseUrl = currentUrl;
        if (currentUrl != previousUrl) this.viewportScroller.scrollToPosition([0, 0]);
      });
  }

  /** Track if we're on a learner route to hide the global header */
  private trackLearnerRoutes() {
    // Check initial route
    this.isLearnerRoute.set(this.router.url.startsWith('/learner') || this.router.url.startsWith('/home'));
    this.isLoginRoute.set(this.router.url === '/login' || this.router.url.startsWith('/login?'));
    this.isAdminRoute.set(this.router.url.startsWith('/admin'));
    this.isTrainerRoute.set(this.router.url.startsWith('/trainer'));

    this.router.events
      .pipe(
        filter((event: any): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(event => {
        const url = event.urlAfterRedirects;
        this.isLearnerRoute.set(url.startsWith('/learner') || url.startsWith('/home'));
        this.isLoginRoute.set(url === '/login' || url.startsWith('/login?'));
        this.isAdminRoute.set(url.startsWith('/admin'));
        this.isTrainerRoute.set(url.startsWith('/trainer'));
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
